import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { Menu, X, ChevronDown, Globe, Sparkles } from 'lucide-react';
import { API } from '@/config/api';

const API_BASE = API;

export const Navbar = ({ isRoot = false, maintenanceMode = false }) => {
  const { t, currentLang, changeLanguage, languages } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  // Check admin authentication on mount
  useEffect(() => {
    // Non-blocking admin session verification
    // Only verify if token exists, and don't block rendering
    const token = localStorage.getItem('admin_token');
    if (token) {
      // Defer verification to not block initial render
      const timeoutId = setTimeout(() => {
        axios.get(`${API_BASE}/admin/verify-session`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(() => setIsAdminAuthenticated(true))
        .catch(() => setIsAdminAuthenticated(false));
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
    
    // Re-check on storage change (for multi-tab sync)
    const handleStorageChange = () => {
      const newToken = localStorage.getItem('admin_token');
      if (newToken) {
        axios.get(`${API_BASE}/admin/verify-session`, {
          headers: { Authorization: `Bearer ${newToken}` }
        })
        .then(() => setIsAdminAuthenticated(true))
        .catch(() => setIsAdminAuthenticated(false));
      } else {
        setIsAdminAuthenticated(false);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const baseNavLinks = [
    { key: 'home', path: '' },
    { key: 'lotteries', path: '/lotteries' },
    { key: 'predictions', path: '/statistical-projections' },
    { key: 'statistics', path: '/statistics' },
    { key: 'about', path: '/about' },
    { key: 'donate', path: '/donate' },
  ];

  // Only add CSV Importer link if admin is authenticated
  const navLinks = isAdminAuthenticated 
    ? [
        ...baseNavLinks.slice(0, 4),
        { key: 'csvImporter', path: '/admin-csv-tools-2025', label: 'Admin Tools' },
        { key: 'contentEditor', path: '/admin/content', label: 'Content Editor' },
        { key: 'pipelineMonitor', path: '/admin/pipeline-monitor', label: 'Pipeline Monitor' },
        ...baseNavLinks.slice(4)
      ]
    : baseNavLinks;

  const handleLanguageChange = (langCode) => {
    // Get current path
    const pathParts = location.pathname.split('/');
    let newPath;
    
    // Handle root path specially
    if (location.pathname === '/' || location.pathname === '') {
      // From root, go to language path (or stay at root for EN)
      newPath = langCode === 'en' ? '/' : `/${langCode}`;
    } else if (pathParts[1] && languages.find(l => l.code === pathParts[1])) {
      // Replace existing language prefix
      pathParts[1] = langCode === 'en' ? '' : langCode;
      newPath = pathParts.filter(p => p).join('/') || '/';
      if (newPath !== '/' && !newPath.startsWith('/')) newPath = '/' + newPath;
    } else {
      // Add language prefix
      newPath = langCode === 'en' ? '/' : `/${langCode}${location.pathname}`;
    }
    
    // Update language context and navigate
    changeLanguage(langCode);
    navigate(newPath);
  };

  const isActive = (path) => {
    // Handle root path
    if (location.pathname === '/' || location.pathname === '') {
      return path === '';
    }
    
    // For English at root (no /en prefix)
    if (currentLang === 'en' && !location.pathname.startsWith('/en')) {
      if (path === '') {
        return location.pathname === '/';
      }
      return location.pathname === path || location.pathname.startsWith(path + '/');
    }
    
    // For language-prefixed paths
    const fullPath = `/${currentLang}${path}`;
    if (path === '') {
      return location.pathname === `/${currentLang}` || location.pathname === `/${currentLang}/`;
    }
    return location.pathname.startsWith(fullPath);
  };

  // Get the base path for links
  // For root path (/) and English language, keep paths without /en prefix
  const isAtRoot = location.pathname === '/' || location.pathname.startsWith('/lottery') || 
                   location.pathname.startsWith('/statistics') || location.pathname.startsWith('/statistical-projections') ||
                   location.pathname.startsWith('/lotteries') || location.pathname.startsWith('/about') ||
                   location.pathname.startsWith('/donate') || location.pathname.startsWith('/law-of-attraction');
  const basePath = (isAtRoot && currentLang === 'en') ? '' : `/${currentLang}`;

  const currentLanguage = languages.find(l => l.code === currentLang);

  return (
    <nav className={`fixed left-0 right-0 z-50 bg-gray-950/95 backdrop-blur-md border-b border-gray-800 ${maintenanceMode ? 'top-10' : 'top-0'}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to={basePath || '/'} 
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <img 
              src="/lotterics-icon.svg" 
              alt="Lotterics" 
              className="w-9 h-9 rounded-lg shadow-lg shadow-amber-500/20 group-hover:shadow-amber-500/30 transition-shadow"
            />
            <span className="text-white font-bold text-xl tracking-tight">
              Lotter<span className="text-amber-400">ics</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              // Use basePath for link generation
              // If basePath is empty (root EN), use direct paths
              const linkPath = link.path === '' 
                ? (basePath || '/') 
                : (basePath ? `${basePath}${link.path}` : link.path);
              return (
              <Link
                key={link.key}
                to={linkPath}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-amber-400 bg-amber-500/10'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
                data-testid={`nav-${link.key}`}
              >
                {link.key === 'lawOfAttraction' && <Sparkles className="w-3.5 h-3.5 inline mr-1.5" />}
                {link.label || t(`nav.${link.key}`)}
              </Link>
              );
            })}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-gray-700 bg-gray-900/50 text-gray-300 hover:text-amber-400 hover:bg-gray-800 hover:border-gray-600 h-9 px-3"
                  data-testid="language-selector"
                >
                  <Globe className="w-4 h-4 mr-2 text-amber-400" />
                  <span className="font-medium">{currentLanguage?.code.toUpperCase()}</span>
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-gray-900 border-gray-700 w-40">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`text-gray-300 hover:text-amber-400 hover:bg-gray-800 cursor-pointer ${
                      currentLang === lang.code ? 'bg-amber-500/10 text-amber-400' : ''
                    }`}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-3 text-lg">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                    {currentLang === lang.code && (
                      <span className="ml-auto w-2 h-2 bg-amber-400 rounded-full" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-gray-400 hover:text-white hover:bg-gray-800 h-9 w-9"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-800 animate-in slide-in-from-top-2">
            <div className="space-y-1">
              {navLinks.map((link) => {
                // Use basePath for link generation
                const linkPath = link.path === '' 
                  ? (basePath || '/') 
                  : (basePath ? `${basePath}${link.path}` : link.path);
                return (
                <Link
                  key={link.key}
                  to={linkPath}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-amber-400 bg-amber-500/10'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.key === 'lawOfAttraction' && <Sparkles className="w-4 h-4 inline mr-2" />}
                  {link.label || t(`nav.${link.key}`)}
                </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
